/**
 * Hook to fetch the rolling 90-day forecast from confirmed forward bookings.
 *
 * Returns monthly forecast buckets keyed by "YYYY-MM" that chart-data builders
 * can merge with actuals. The forecast endpoint returns GBP-denominated
 * figures; callers needing EUR cross-reference must apply FX themselves.
 *
 * Forecast is NOT a prediction. It sums what is already booked. The dashed
 * line on charts should be read as a floor, not a projection.
 */
import { useState, useEffect } from 'react';
import { forecastAPI } from '../lib/api';

/**
 * @param {Object} params
 * @param {string} params.scope - 'portfolio' | 'entity' | 'property'
 * @param {string} [params.id] - entity or property ID (required for entity/property scope)
 * @param {string} [params.region] - 'all' | 'uk' | 'fr'
 * @param {number} [params.year] - only fetch when this is the current year
 *   (forecast is only meaningful for the current calendar year)
 * @returns {{ forecastData: object|null, forecastByMonth: Record<string, object>, isLoading: boolean }}
 */
export function useForecast({ scope = 'portfolio', id, region = 'all', year } = {}) {
  const [forecastData, setForecastData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    // Only fetch forecast for the current year
    if (year != null && year !== currentYear) {
      setForecastData(null);
      return;
    }

    let cancelled = false;
    const fetchForecast = async () => {
      setIsLoading(true);
      try {
        const params = { scope, region };
        if (id) params.id = id;
        const res = await forecastAPI.get(params);
        if (!cancelled) setForecastData(res.data);
      } catch {
        // Forecast is supplementary; failure should not break the page
        if (!cancelled) setForecastData(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchForecast();
    return () => { cancelled = true; };
  }, [scope, id, region, year]);

  // Build a month-keyed lookup: { "2026-06": { revenue_gbp, margin_gbp, occupancy_pct, adr_gbp, ... } }
  const forecastByMonth = {};
  if (forecastData?.monthly) {
    for (const m of forecastData.monthly) {
      forecastByMonth[m.month] = m;
    }
  }

  return { forecastData, forecastByMonth, isLoading };
}
